<template>
  <profile-edit-section-card
    v-if="localState"
    title="Experience"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    :has-invalid-input="hasInvalidInput"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <poppins-text-field
      id="years-experience"
      v-model="localState.yearsExperience"
      label-name="Years of paid child care experience"
      type="text"
      pattern="[0-9]*"
      inputmode="numeric"
      :limit-input-to-pattern="true"
      :helper-text="yearsExperienceHelperText"
      :status="yearsExperienceStatus"
      class="mb-24"
    />
    <fieldset>
      <legend class="form__label pb-8">What ages do you have experience with?</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <toggle-token-group
        custom-id="age-group-tokens"
        :tokens="ageGroupTokens"
        :max-columns="2"
        :status="ageGroupsStatus"
        :helper-text="ageGroupsHelperText"
        legend="What ages do you have experience with?"
        @update:model-value="updateAgeGroupTokens"
      />
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsTextField from '@/components/textfield/TextField.vue';
import ToggleTokenGroup from '@/components/toggle_token/ToggleTokenGroup.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import type { BabysitterProfileFormState } from '../../__types__/BabysitterProfileTypes';
import { childCareAgeGroupsToggleTokenOptions } from '@/apps/sitter/signup/profile/components/__types__/AgeTypes';
import HelperStatus from '@/helpers/status';

export default {
  name: 'ChildCareExperienceForm',
  components: {
    ProfileEditSectionCard,
    PoppinsTextField,
    ToggleTokenGroup,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      hasInvalidInput: false,
      yearsExperienceStatus: HelperStatus.Neutral,
      ageGroupsStatus: HelperStatus.Neutral,
    };
  },
  computed: {
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
    yearsExperienceHelperText(): string {
      if (!this.hasInvalidInput) return '';
      if (!this.localState?.yearsExperience) {
        return 'Years of experience is required';
      }
      if (isNaN(Number(this.localState.yearsExperience)) || Number(this.localState.yearsExperience) <= 0) {
        return 'Years of experience must be at least 1';
      }
      if (Number(this.localState.yearsExperience) > 50) {
        return 'Years of experience cannot be over 50';
      }
      return '';
    },
    ageGroupsHelperText(): string | null {
      if (this.ageGroupsStatus === HelperStatus.Error) {
        return 'Selecting at least one age is required.';
      }
      return null;
    },
    ageGroupTokens(): Object[] {
      const selectedAgeGroups = this.localState?.ageGroups || [];
      return childCareAgeGroupsToggleTokenOptions.map((option) => ({
        ...option,
        value: selectedAgeGroups.includes(option.type),
      }));
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
    'localState.yearsExperience': function (newValue) {
      if (newValue && this.yearsExperienceStatus === HelperStatus.Error) {
        this.yearsExperienceStatus = HelperStatus.Neutral;
        this.hasInvalidInput = this.ageGroupsStatus === HelperStatus.Error;
      }
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as BabysitterProfileFormState | null;
      if (!state) return;

      this.localState = {
        yearsExperience: String(state.yearsExperience ?? 0),
        ageGroups: [...(state.ageGroups || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    updateAgeGroupTokens(newTokens) {
      const newValue = [] as string[];
      newTokens.forEach((option) => {
        if (option.value) newValue.push(option.type);
      });
      if (this.ageGroupsStatus === HelperStatus.Error && newValue.length) {
        this.ageGroupsStatus = HelperStatus.Neutral;
        this.hasInvalidInput = this.yearsExperienceStatus === HelperStatus.Error;
      }
      this.localState.ageGroups = newValue;
    },
    isYearsExperienceInvalid(): boolean {
      return (
        !this.localState.yearsExperience ||
        isNaN(Number(this.localState.yearsExperience)) ||
        Number(this.localState.yearsExperience) <= 0 ||
        Number(this.localState.yearsExperience) > 50
      );
    },
    async handleSave() {
      if (!this.localState) return;

      const yearsExperienceInvalid = this.isYearsExperienceInvalid();
      const ageGroupsInvalid = !this.localState.ageGroups?.length;

      if (yearsExperienceInvalid) {
        this.yearsExperienceStatus = HelperStatus.Error;
      }
      if (ageGroupsInvalid) {
        this.ageGroupsStatus = HelperStatus.Error;
      }
      if (yearsExperienceInvalid || ageGroupsInvalid) {
        this.hasInvalidInput = true;
        this.$nextTick(() => {
          const element = document.getElementById(yearsExperienceInvalid ? 'years-experience' : 'age-group-tokens-helper');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
      if (this.hasUnsavedChanges) {
        await this.saveSection({
          yearsExperience: Number(this.localState.yearsExperience),
          ageGroups: [...this.localState.ageGroups],
        });
      }
    },
  },
};
</script>
